import { mapEditorFetch } from '../utils/fetch';
import { DrawVehicle } from '@/map-core/drawRectangle';
import { CoordType } from '@/utils/enum';
import { HttpStatusCode } from '@/fetch/core/constant';
import type MapEngine from '@/map-core';

/**
 * 车辆框覆盖物管理器
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 根据车辆位置和朝向，请求车辆尺寸并绘制车辆框（矩形 + 朝向线 + 基线）
 * 2. 提供清除车辆框的统一入口
 *
 * 不直接访问 store，通过回调与 controller 协作
 */
export class VehicleOverlayManager {
  private drawVehicle: any = null;

  constructor(
    private getMap: () => MapEngine,
    private clearRectangleRef: () => void
  ) {}

  /** 清除车辆框 */
  clearRectangle() {
    this.drawVehicle?.clearRectangle();
    this.drawVehicle = null;
  }

  /**
   * 根据经纬度和朝向绘制车辆框
   * 会先请求车辆尺寸，再创建覆盖物
   */
  async drawVehicleRectangle(info: { lon: number; lat: number; heading: number }) {
    const map = this.getMap();
    const res = await mapEditorFetch.getSizeOfVehicle();
    if (res.code !== HttpStatusCode.Success) return;

    const vehicleTypeData = {
      width: Number(res.data?.width ?? 0),
      frontEdgeToCenter: Number(res.data?.frontEdgeToCenter ?? 0),
      backEdgeToCenter: Number(res.data?.backEdgeToCenter ?? 0),
    };
    const vehicleLength = vehicleTypeData.frontEdgeToCenter + vehicleTypeData.backEdgeToCenter;
    const lineLength = Math.max(vehicleLength * 1.5, 8);

    this.clearRectangle();

    this.drawVehicle = new DrawVehicle({
      lon: info.lon,
      lat: info.lat,
      heading: info.heading,
      mapEngineRef: map,
    });
    this.drawVehicle.drawDirectionRectangle(
      info.lon,
      info.lat,
      info.heading,
      vehicleTypeData,
      'inner'
    );
    this.drawVehicle.drawDirectionLine(lineLength);
    this.drawVehicle.drawBaseLine(lineLength);
  }

  /**
   * 获取基础位置信息并更新地图
   * 如果是车辆类型，还会绘制车辆框
   */
  async getBasicPosition(
    stationNum: string,
    updateMapCenter: (pos: { lon: number; lat: number }) => void,
    searchType: string
  ) {
    const res = await mapEditorFetch.fetchPostionInfo({
      metaType: searchType,
      key: stationNum,
      positionType: CoordType.GCJ02,
    });
    if (
      res.code === HttpStatusCode.Success &&
      res.data?.longitude &&
      res.data?.latitude
    ) {
      updateMapCenter({
        lon: res.data.longitude,
        lat: res.data.latitude,
      });
      this.clearRectangle();
      if (searchType === 'vehicle' && res.data?.radian) {
        await this.drawVehicleRectangle({
          lon: res.data.longitude,
          lat: res.data.latitude,
          heading: res.data.radian,
        });
      }
      return true;
    }
    return false;
  }
}
